import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';

/**
 * Styling for the Billing card: the cancellation-state banner, the
 * additional-components pill row, and the read-mode action button row. All tokens come
 * from `THEME`; literal pixel values (the 9px banner dot, the 36px action buttons) are
 * exact hifi-design requirements with no scale match. The status pill sits inline with
 * the title via `OverviewCard`'s `headerAccessory` slot, so it needs no row style here.
 */
export const billingCardStyles = StyleSheet.create({
	banner: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		gap: THEME.spacing.sm + 2,
		padding: `${THEME.spacing.sm + 3}px ${THEME.spacing.md}px`,
	},
	bannerDot: {
		backgroundColor: THEME.colors.success.dark,
		borderRadius: '50%',
		flex: 'none',
		height: 9,
		width: 9,
	},
	bannerDotCancelled: {
		backgroundColor: THEME.colors.danger.main,
	},
	// Red rather than warning orange: Success read orange as advisory and scrolled past it.
	bannerDotPendingCancel: {
		backgroundColor: THEME.colors.danger.main,
	},
	// Inline alert beside the plan name when Chargify reports the subscription past due.
	pastDueText: {
		color: THEME.colors.danger.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	trialActionButton: {
		display: 'block',
		fontSize: THEME.typography.fontSizes.xs,
		marginTop: THEME.spacing.xs,
	},
	trialExtendedText: {
		color: THEME.colors.success.dark,
		fontSize: THEME.typography.fontSizes.xxs,
	},
	pendingBillingCycleChangeText: {
		color: THEME.colors.warning.dark,
		fontSize: THEME.typography.fontSizes.xs,
		fontStyle: 'italic',
		marginTop: THEME.spacing.xs,
	},
	bannerText: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	componentPills: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
	},
	actionRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm + 4,
	},
	actionButton: {
		height: 36,
	},
	// Plan-cell edit affordance (Chargify ID / link-to-master): input stacked over its toggle link.
	planEdit: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	planLinkToggle: {
		height: 'auto',
		padding: 0,
		width: 'fit-content',
	},
	// Read-only "Main: <linked account>" line shown for a Comp account already linked to a master.
	mainAccountLink: {
		color: THEME.colors.text.main,
		display: 'block',
		fontSize: THEME.typography.fontSizes.sm,
		marginTop: THEME.spacing.xs,
	},
	mainAccountAnchor: {
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
});
